// components/timesheet/TimesheetTable.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiEye, FiCheck, FiX, FiEdit, FiTrash } from 'react-icons/fi';
import dayjs from 'dayjs';

const TimesheetTable = ({
  timesheets,
  pagination,
  page,
  isLoading,
  handleView,
  handleApprove,
  handleReject,
  handleEdit,
  handleDelete,
  getStatusBadge,
  getAttendanceIcon,
  formatTime
}) => {
  const { t } = useTranslation();
  const formatWeekDisplay = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
  };

  // ═══ Get the display value for a day cell ═══
  const getDayCellDisplay = (entry) => {
    if (!entry) return <span className="text-gray-300">-</span>;
    
    // If on Leave of Absence - show the badge
    if (entry.isOnLeave) {
      if (entry.leaveType === 'paid_leave_of_absence') {
        return (
          <span title='Paid leave of absence' className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800 rounded">
            PLOA
          </span>
        );
      }
      if (entry.leaveType === 'unpaid_leave_of_absence') {
        return (
          <span title='Unpaid leave of absence' className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-800 rounded">
            ULOA
          </span>
        );
      }
      if (entry.leaveType === 'paid_leave') {
        return (
          <span title='Paid Leave' className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 rounded">
            PL
          </span>
        );
      }
      if (entry.leaveType === 'unpaid_leave') {
        return (
          <span title='Unpaid Leave' className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-800 rounded">
            UL
          </span>
        );
      }
    }
    
    // If has punch times - show them
    if (entry.punchInDateTime || entry.punchOutDateTime) {
      return (
        <div className="flex flex-col items-center gap-0.5">
          <div className="text-xs font-medium text-gray-700">
            {formatTime(entry.punchInDateTime)}
          </div>
          <div className="text-xs text-gray-400">
            {formatTime(entry.punchOutDateTime)}
          </div>
          <div className="mt-0.5">
            {getAttendanceIcon(entry)}
          </div>
        </div>
      );
    }
    
    // Otherwise show attendance status
    if (entry.isAbsent) {
      return <span className="text-red-600 font-bold text-xs">A</span>;
    }
    
    // Default: show checkmark for present
    return <span className="text-green-600">✓</span>;
  };

  // ═══ Get colored leave summary badge for Regular Hrs column ═══
  const getLeaveSummaryBadge = (ts) => {
    const badges = [];
    
    if (ts.totalPaidLeaveDays > 0) {
      badges.push(
        <span title="Paid Leave of Absence" key="paid" className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800 rounded">
          PLOA: {ts.totalPaidLeaveDays}d
        </span>
      );
    }
    
    if (ts.totalUnpaidLeaveDays > 0) {
      badges.push(
        <span title="Unpaid Leave of Absence" key="unpaid" className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-800 rounded">
          ULOA: {ts.totalUnpaidLeaveDays}d
        </span>
      );
    }
    
    if (badges.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-0.5 justify-center">
        {badges}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-3">
        <div className="border border-gray-200 overflow-x-auto rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-gray-700 bg-gray-50">
              <tr>
                <th className="px-4 py-3 font-medium w-12">#</th>
                <th className="px-4 py-3 font-medium min-w-[150px]">{t("EMPLOYEE")}</th>
                <th className="px-4 py-3 font-medium min-w-[120px]">{t("WEEK")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("MON")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("TUE")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("WED")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("THU")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("FRI")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("SAT")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("SUN")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[100px]">{t("REGULAR_HRS")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[80px]">{t("OVERTIME_HRS")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[100px]">{t("STATUS")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[150px]">{t("ACTION")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={14} className="text-center py-8">
                  <div className="flex justify-center items-center">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (timesheets.length === 0) {
    return (
      <div className="p-3">
        <div className="border border-gray-200 overflow-x-auto rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-gray-700 bg-gray-50">
              <tr>
                <th className="px-4 py-3 font-medium w-12">Sno.</th>
                <th className="px-4 py-3 font-medium min-w-[150px]">{t("EMPLOYEE")}</th>
                <th className="px-4 py-3 font-medium min-w-[120px]">{t("WEEK")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("MON")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("TUE")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("WED")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("THU")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("FRI")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("SAT")}</th>
                <th className="px-2 py-3 font-medium text-center w-12">{t("SUN")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[100px]">{t("REGULAR_HRS")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[80px]">{t("OVERTIME_HRS")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[100px]">{t("STATUS")}</th>
                <th className="px-4 py-3 font-medium text-center min-w-[150px]">{t("ACTION")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={14} className="text-center py-8 text-gray-400">
                  {t("NO_TIMESHEETS_FOUND")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="border border-gray-200 overflow-x-auto rounded-lg">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-gray-700 bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium w-12">Sno.</th>
              <th className="px-4 py-3 font-medium min-w-[150px]">{t("EMPLOYEE")}</th>
              <th className="px-4 py-3 font-medium min-w-[120px]">{t("WEEK")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("MON")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("TUE")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("WED")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("THU")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("FRI")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("SAT")}</th>
              <th className="px-2 py-3 font-medium text-center w-12">{t("SUN")}</th>
              <th className="px-4 py-3 font-medium text-center min-w-[100px]">{t("REGULAR_HRS")}</th>
              <th className="px-4 py-3 font-medium text-center min-w-[80px]">{t("OVERTIME_HRS")}</th>
              <th className="px-4 py-3 font-medium text-center min-w-[100px]">{t("STATUS")}</th>
              <th className="px-4 py-3 font-medium text-center min-w-[150px]">{t("ACTION")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {timesheets.map((ts, index) => {
              const weekStart = dayjs(ts.weekStartDate);
              
              return (
                <tr key={ts._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {(page - 1) * 10 + index + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{ts.employeeId?.name}</div>
                    <div className="text-gray-500 text-xs">{ts.employeeId?.employeeId}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatWeekDisplay(ts.weekStartDate, ts.weekEndDate)}
                  </td>
                  {/* Day columns */}
                  {[0, 1, 2, 3, 4, 5, 6].map((dayIdx) => {
                    const dayDate = weekStart.add(dayIdx, 'day');
                    const entry = ts.dailyEntries?.find(
                      (e) => dayjs(e.date).format('YYYY-MM-DD') === dayDate.format('YYYY-MM-DD')
                    );
                    
                    return (
                      <td key={dayIdx} className="px-2 py-3 text-center">
                        {getDayCellDisplay(entry)}
                      </td>
                    );
                  })}
                  {/* Regular Hrs - with colored leave summary badges */}
                  <td className="px-3 py-3 text-center">
                    <div className="font-medium text-gray-900">
                      {ts.totalRegularHours}h
                    </div>
                    {getLeaveSummaryBadge(ts)}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">
                    <span
                      className={
                        ts.totalOvertimeHours > 0
                          ? "text-orange-600"
                          : "text-gray-400"
                      }
                    >
                      {ts.totalOvertimeHours}h
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {getStatusBadge(ts.status)}
                    {ts.rejectionReason && (
                      <div className="text-xs text-red-600 mt-1 truncate max-w-[120px]">
                        {ts.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-center gap-1 flex-wrap">
                      <button
                        onClick={() => handleView(ts)}
                        className="p-1.5 hover:bg-blue-50 rounded text-blue-600 transition-colors duration-150"
                        title={t("VIEW_DETAILS")}
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      {ts.status === "submitted" && (
                        <>
                          <button
                            onClick={() => handleApprove(ts._id)}
                            className="p-1.5 hover:bg-green-50 rounded text-green-600 transition-colors duration-150"
                            title={t("APPROVE")}
                          >
                            <FiCheck className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(ts._id)}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600 transition-colors duration-150"
                            title={t("REJECT")}
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleEdit(ts, 0)}
                        className="p-1.5 hover:bg-yellow-50 rounded text-yellow-600 transition-colors duration-150"
                        title={t("EDIT")}
                      >
                        <FiEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(ts._id)}
                        className="p-1.5 hover:bg-red-50 rounded text-red-600 transition-colors duration-150"
                        title={t("DELETE")}
                      >
                        <FiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimesheetTable;